/**
 * Strip Markdown syntax from text, returning plain text.
 * Handles: headers, bold, italic, links, images, code, blockquotes, lists, and inline HTML.
 */
export function stripMarkdown(markdown: string | null | undefined): string {
  if (!markdown) return '';

  let text = markdown;

  // Remove blockquotes (lines starting with >)
  text = text.replace(/^>\s+/gm, '');

  // Remove headers (# ## ###) — at line start (optional space so "##foo" and "## foo" both strip)
  text = text.replace(/^#{1,6}\s*/gm, '');
  // Inline "header" style: space + 1–6 # + optional space
  text = text.replace(/\s+#{1,6}\s*/g, ' ');

  // Remove horizontal rules (--- or ***)
  text = text.replace(/^[-*]{3,}$/gm, '');

  // Remove fenced code blocks (```code```) — keep content as plain text
  text = text.replace(/```[\s\S]*?```/g, (match) => {
    const inner = match.replace(/^```\w*\n?|\n?```$/g, '').trim();
    return inner.length ? inner + ' ' : '';
  });

  // Remove inline code (`code`) — keep content
  text = text.replace(/`([^`]+)`/g, '$1');

  // Remove images ![alt](url) — keep alt text
  text = text.replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1');

  // Remove links [text](url) — keep link text
  text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  // Reference-style links [text][ref] — keep link text
  text = text.replace(/\[([^\]]+)\]\s*\[[^\]]*\]/g, '$1');

  // Angle-bracket autolinks <https://example.com> — keep URL as plain text
  text = text.replace(/<(https?:\/\/[^>]+)>/g, '$1');

  // Remove bold (**text** or __text__)
  text = text.replace(/\*\*([^*]+)\*\*/g, '$1');
  text = text.replace(/__([^_]+)__/g, '$1');

  // Remove italic (*text* or _text_) — run after bold so nested works
  text = text.replace(/\*([^*]+)\*/g, '$1');
  text = text.replace(/_([^_]+)_/g, '$1');

  // Remove strikethrough (~~text~~)
  text = text.replace(/~~([^~]+)~~/g, '$1');

  // Remove list markers (-, *, +, 1., 2., etc.)
  text = text.replace(/^[\s]*[-*+]\s+/gm, '');
  text = text.replace(/^[\s]*\d+\.\s+/gm, '');

  // Strip HTML tags (e.g. <em>, <a href="...">...</a>) — keep inner text only
  text = text.replace(/<[^>]+>/g, '');

  // Clean up multiple spaces and newlines
  text = text.replace(/\n{3,}/g, '\n\n');
  text = text.replace(/[ \t]+/g, ' ');
  text = text.trim();

  return text;
}

